import { FC, useEffect, useState } from "react";
import { AuthBrandLogo } from "components/AuthBrandLogo";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useVerifyRegistrationToken, useSetInitialPassword } from "hooks/useAuth";
import { useAppDispatch } from "store";
import { setAuth, setProfileAndContact } from "store/slices/authSlice";
import { getAuthProfile, requestKeepAlive } from "services/authAPI";
import { getFirebaseToken } from "services";
import styled from "@emotion/styled";
import {
  Button,
  Col,
  Form,
  Input,
  message,
  Row,
  Spin,
  Typography,
  Result,
} from "antd";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";

type PageState = "loading" | "form" | "error" | "success";

const VerifyEmailPage: FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();

  const [pageState, setPageState] = useState<PageState>("loading");
  const [userName, setUserName] = useState<string>("");

  const { mutate: verifyToken, isPending: isVerifying } = useVerifyRegistrationToken();
  const { mutate: submitPassword, isPending: isSubmitting } = useSetInitialPassword();

  useEffect(() => {
    if (!token) {
      setPageState("error");
      return;
    }

    verifyToken(
      { token },
      {
        onSuccess: (data) => {
          if (data.status === "success") {
            const name = [data.first_name, data.last_name].filter(Boolean).join(" ");
            setUserName(name);
            setPageState("form");
          } else {
            setPageState("error");
          }
        },
        onError: () => {
          setPageState("error");
        },
      }
    );
  }, []);

  const handleGetFirebaseToken = () => {
    getFirebaseToken()
      .then((firebaseToken) => {
        if (firebaseToken) {
          requestKeepAlive({ fcm_token: firebaseToken });
        }
      })
      .catch((err) =>
        console.error("An error occured while retrieving firebase token. ", err)
      );
  };

  const handleSubmit = (values: { password: string; confirmPassword: string }) => {
    if (!token) return;

    submitPassword(
      { token, new_password: values.password },
      {
        onSuccess: (data) => {
          if (data.status === "success" && data.access_token) {
            dispatch(setAuth({ access_token: data.access_token }));

            getAuthProfile()
              .then((res) => {
                if (res.status === "success") {
                  dispatch(
                    setProfileAndContact({
                      profile: res.profile,
                      contact: res.contact,
                    })
                  );
                  //handleGetFirebaseToken();
                  setPageState("success");
                  message.success("ยืนยันตัวตนและตั้งรหัสผ่านสำเร็จ");
                  // ไม่เด้งอัตโนมัติแล้ว — หน้าถัดไปมีขั้นตอนให้อ่าน 1.5 วิไม่พอ
                  // ให้กดปุ่มเองตามหลัก CTA เดียวในเอกสาร Journey ของลูกค้า
                }
              })
              .catch((err: any) => {
                message.error(`${err}`);
                dispatch(setAuth({}));
              });
          } else {
            message.error(data.error_message || "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง");
          }
        },
        onError: () => {
          message.error("เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง");
        },
      }
    );
  };

  const renderContent = () => {
    switch (pageState) {
      case "loading":
        return (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Typography.Text type="secondary">กำลังตรวจสอบลิงก์...</Typography.Text>
            </div>
          </div>
        );

      case "error":
        return (
          <div style={{ textAlign: "center" }}>
            <Result
              status="error"
              title="ลิงก์หมดอายุหรือไม่ถูกต้อง"
              subTitle="กรุณาขอลิงก์ยืนยันตัวตนใหม่ หรือติดต่อทีมงาน"
            />
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, marginTop: 16 }}>
              <Link to="/resend-verify-email">ขอลิงก์ยืนยันตัวตนใหม่</Link>
              <Link to="/sign-in">กลับหน้าเข้าสู่ระบบ</Link>
            </div>
          </div>
        );

      case "success":
        return (
          <div style={{ textAlign: "center" }}>
            <Result
              status="success"
              title="ยืนยันตัวตนสำเร็จ"
              subTitle="บัญชีผู้ขายของท่านพร้อมใช้งานแล้ว"
              extra={
                <Button type="primary" size="large" onClick={() => navigate("/dashboard")}>
                  เข้าสู่ระบบผู้ขาย
                </Button>
              }
            />

            {/* ขั้นตอนถัดไป ตามเอกสาร Journey ของลูกค้าหัวข้อ 7
                ยังไม่ติ๊กอัตโนมัติจากข้อมูลจริง — ต้องมี endpoint ให้ผู้ขายอ่าน stage
                ตัวเองก่อน (ตอนนี้ตัวคำนวณ stage เปิดให้เฉพาะฝั่งแอดมิน)
                เอาไว้ค่อยต่อเมื่อมีคนบอกว่ารายการนิ่ง ๆ ไม่พอ */}
            <div
              style={{
                margin: "0 auto",
                maxWidth: 420,
                textAlign: "left",
                background: "rgba(38,119,200,0.05)",
                borderRadius: 8,
                padding: 16,
              }}
            >
              <Typography.Text strong>ขั้นตอนถัดไป</Typography.Text>
              <ol style={{ margin: "8px 0 0", paddingLeft: 20, lineHeight: 1.9 }}>
                <li>ตรวจสอบข้อมูลร้านค้า</li>
                <li>เพิ่มสินค้าเข้าสู่ระบบ</li>
                <li>ตรวจสอบข้อมูลและส่งสินค้าเพื่อพิจารณาอนุมัติ</li>
                <li>เมื่อร้านค้าและสินค้าได้รับการอนุมัติ เริ่มจำหน่ายได้ทันที</li>
              </ol>
            </div>
          </div>
        );

      case "form":
        return (
          <>
            <SectionText>
              <Typography.Title level={4}>ยืนยันตัวตนและตั้งรหัสผ่าน</Typography.Title>
            </SectionText>
            {userName && (
              <SectionText>
                <Typography.Text type="secondary">
                  สวัสดี, {userName}
                </Typography.Text>
              </SectionText>
            )}
            <SectionText>
              <Typography.Text>กรุณาตั้งรหัสผ่านเพื่อเริ่มใช้งานระบบ</Typography.Text>
            </SectionText>

            <SectionInput style={{ marginTop: 20 }}>
              <Form
                onFinish={handleSubmit}
                layout="vertical"
                form={form}
                style={{ display: "flex", flexDirection: "column", gap: 8 }}
              >
                <Form.Item
                  name="password"
                  rules={[
                    { required: true, message: "กรุณากรอกรหัสผ่าน" },
                    { min: 6, message: "รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร" },
                    { max: 50, message: "รหัสผ่านต้องไม่เกิน 50 ตัวอักษร" },
                  ]}
                >
                  <Input.Password
                    placeholder="รหัสผ่าน"
                    autoComplete="new-password"
                    size="large"
                    iconRender={(visible) =>
                      visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                    }
                  />
                </Form.Item>

                <Form.Item
                  name="confirmPassword"
                  dependencies={["password"]}
                  rules={[
                    { required: true, message: "กรุณายืนยันรหัสผ่าน" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("password") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error("รหัสผ่านไม่ตรงกัน"));
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    placeholder="ยืนยันรหัสผ่าน"
                    autoComplete="new-password"
                    size="large"
                    iconRender={(visible) =>
                      visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                    }
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    style={{ width: "100%" }}
                    loading={isSubmitting}
                    size="large"
                  >
                    ยืนยันและเข้าสู่ระบบ
                  </Button>
                </Form.Item>
              </Form>

              <div style={{ textAlign: "center", marginTop: 16 }}>
                <Link to="/sign-in">กลับหน้าเข้าสู่ระบบ</Link>
              </div>
            </SectionInput>
          </>
        );
    }
  };

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#fff" }}>
      <Row gutter={[0, 0]}>
        <Col
          style={{ padding: 0 }}
          xs={{ span: 0 }}
          lg={{ span: 16 }}
          md={{ span: 16 }}
        >
          <BackgroundGradient>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100vh",
              }}
            >
              <AuthBrandLogo />
            </div>
          </BackgroundGradient>
        </Col>
        <Col
          xs={{ span: 24, offset: 0 }}
          lg={{ span: 8, offset: 0 }}
          md={{ span: 8 }}
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            height: "100vh",
          }}
        >
          {renderContent()}
        </Col>
      </Row>
    </div>
  );
};

export default VerifyEmailPage;

const SectionText = styled.div`
  width: 100%;
  margin-top: 10px;
  text-align: center;
  padding: 0 5px;
`;

const SectionInput = styled.div`
  padding: 0 20px;
`;

const BackgroundGradient = styled.div`
  background: linear-gradient(45deg, #2777c8, #fff);
  width: 100%;
  height: 100vh;
`;
